"use client";

import { useMemo, useRef, useState } from "react";
import {
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SMALL, HINT } from "@/components/tool-kit/styles";
import {
  type Criterion,
  RocSummary,
} from "@/components/tools/stats-clinical/roc-results";
import { PasteBox } from "@/components/tools/stats-research/paste-box";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseNumberInput, parseNumberList } from "@/lib/parse-number";
import { rocAnalysis } from "@/lib/tool-kit/stats-clinical/roc";
import {
  distinctClasses,
  guessPositiveLabel,
  readScoreRows,
  splitByClass,
} from "@/lib/tool-kit/stats-clinical/roc-input";
import {
  type HeaderMode,
  parseLabelTable,
} from "@/lib/tool-kit/stats-research/matrix";

/** English strings. A translated content file overrides these through `ui`. */
export const ROC_CURVE_CALCULATOR_STRINGS = {
  modeLabel: "Your data",
  modeColumns: "Score and class in two columns",
  modeLists: "Separate lists for each class",
  columnsLabel:
    "Data — one subject per row: test score, then reference-standard class",
  columnsHint:
    "Paste two columns from a spreadsheet, or separate values with tabs, commas or spaces. The class column must hold exactly two different labels, such as 1 and 0 or Disease and Healthy. A header row is optional; a row with a missing value is left out.",
  columnsPlaceholder: "0.8\tDisease\n0.4\tHealthy\n1.5\tDisease",
  listPositiveLabel: "Scores of the positive group (cases)",
  listNegativeLabel: "Scores of the negative group (non-cases)",
  listHint: "Numbers separated by commas, spaces or new lines.",
  listPlaceholder: "0.8, 1.5, 2.2, 0.7",
  loadExample: "Load example",
  clear: "Clear",
  headerLabel: "Header row",
  headerAuto: "Detect automatically",
  headerYes: "First row is a header",
  headerNo: "No header row",
  examplePositiveClass: "Disease",
  exampleNegativeClass: "Healthy",
  exampleHeader: "Score\tStatus",
  positiveClassLabel: "Which class is positive?",
  directionLabel: "A higher score means",
  directionHigher: "More likely positive",
  directionLower: "Less likely positive",
  criterionLabel: "Optimal cut-off by",
  criterionYouden: "Youden's J",
  criterionTopLeft: "Closest to the top-left corner",
  cutoffLabel: "Check a cut-off of your own (optional)",
  cutoffHint:
    "See sensitivity, specificity and predictive values at a cut-off you choose.",
  groupsRead:
    "{positive} positive and {negative} negative, {total} subjects in all.",
  droppedRows:
    "one: {count} row with a missing value was left out.|other: {count} rows with a missing value were left out.",
  unreadable: "Line {line}: “{text}” is not a number.",
  needTwoClasses:
    "The class column must hold exactly two different labels; this one holds {count}.",
  needTwoEach:
    "An ROC curve needs at least two subjects in each class, positive and negative.",
  enterPrompt:
    "Paste test scores with each subject's true class to draw the ROC curve and get the AUC.",
  results: "Results",
  plotLabel: "ROC curve with an area under the curve of {auc}",
  xAxis: "1 − specificity (false positive rate)",
  yAxis: "Sensitivity (true positive rate)",
  aucLabel: "AUC = {auc}",
  ruleAtLeast: "score ≥ {value}",
  ruleAtMost: "score ≤ {value}",
  downloadSvg: "Download SVG",
  downloadPng: "Download PNG",
  downloadCsv: "Download ROC points (CSV)",
  aucCard: "Area under the curve (AUC)",
  ciCard: "95% CI (DeLong)",
  pCard: "p (AUC = 0.5)",
  interpretation:
    "AUC = {auc} is {band} discrimination on Hosmer and Lemeshow's rule of thumb.",
  interpretationNote:
    "0.5 is no better than chance and 1 is perfect. The AUC says how well the score ranks cases above non-cases, not how well any one cut-off works.",
  bandNone: "no better than chance",
  bandPoor: "poor",
  bandAcceptable: "acceptable (0.7 to 0.8)",
  bandExcellent: "excellent (0.8 to 0.9)",
  bandOutstanding: "outstanding (above 0.9)",
  belowChance:
    'The AUC is below 0.5, which usually means the direction is reversed: try "Less likely positive".',
  columnMethod: "Method",
  columnSe: "Standard error",
  columnInterval: "95% CI",
  columnMeasure: "Measure",
  columnEstimate: "Estimate",
  methodDelong: "DeLong (1988)",
  methodHanley: "Hanley & McNeil (1982)",
  optimumHeading: "Optimal cut-off by {criterion}",
  optimumText:
    "Calling a subject positive when {rule} gives sensitivity {sens} and specificity {spec}, with Youden's J = {j}.",
  optimumNote:
    "A cut-off chosen on the same data that estimates its performance is optimistic. Confirm it on new data, and weigh the cost of false positives against false negatives rather than relying on J alone. Predictive values describe this sample's prevalence only.",
  customHeading: "At your cut-off ({rule})",
  sensitivity: "Sensitivity",
  specificity: "Specificity",
  ppv: "Positive predictive value",
  npv: "Negative predictive value",
  lrPositive: "Positive likelihood ratio",
  lrNegative: "Negative likelihood ratio",
  youdenJ: "Youden's J",
  pointsHeading: "Every point on the curve",
  pointsNoteAtLeast:
    "Each row calls a subject positive when the score is at or above the cut-off.",
  pointsNoteAtMost:
    "Each row calls a subject positive when the score is at or below the cut-off.",
  pointsMore:
    "Showing the first {shown} of {total} points; the CSV has them all.",
  columnCutoff: "Cut-off",
  columnSens: "Sensitivity",
  columnSpec: "Specificity",
  columnJ: "Youden's J",
  howCalculated: "How this was calculated",
  workingAuc: "AUC = U / (m × n) = {u} / ({m} × {n}) = {auc}",
  workingSe:
    "DeLong standard error = {se}; 95% CI = AUC ± 1.96 × SE = {lower} to {upper}",
  workingNote:
    "The curve has one point per distinct score; tied scores count one half in the AUC. DeLong's variance uses each subject's placement value (the share of the other class it outranks). The p-value is the Mann–Whitney normal approximation with tie correction. Intervals are held inside 0 to 1.",
  report:
    "AUC = {auc}, 95% CI [{lower}, {upper}] (DeLong), p {p}; optimal cut-off {rule}: sensitivity {sens}, specificity {spec}.",
  copyResult: "Copy result",
  copied: "Copied!",
} as const;

type Mode = "columns" | "lists";

const EXAMPLE_POSITIVE = [
  0.8, 1.5, -0.7, 3.7, 1.3, 0.7, 2, 0.8, 2.2, 0.2, 0.7, 1.6, -1, 0.8, 0.3, 0.7,
  1.1, 2.5, 2.7, 0.6, 1.3, 2.1, 1.3, 2.8,
];
const EXAMPLE_NEGATIVE = [
  0.4, -0.1, 0.6, -2.5, -0.7, 0.4, 0.7, -0.4, -0.3, 0.3, 0.3, -2.2, 0.2, -0.9,
  1, 1.1, -0.5, -0.3, 0.6, 1, 1.1, 0, 1.4, -0.8, 1.6, 0.2, 0, 0.5, 0.5, -1.3,
  0.3,
];

interface Groups {
  positives: number[];
  negatives: number[];
}

function resolveGroups({
  mode,
  read,
  classes,
  positiveLabel,
  positiveText,
  negativeText,
  strings,
  fmt,
}: {
  mode: Mode;
  read: ReturnType<typeof readScoreRows>;
  classes: string[];
  positiveLabel: string;
  positiveText: string;
  negativeText: string;
  strings: typeof ROC_CURVE_CALCULATOR_STRINGS;
  fmt: (value: number) => string;
}): { groups: Groups | null; problem: string | null } {
  if (mode === "lists") {
    const any = positiveText.trim() !== "" || negativeText.trim() !== "";
    return {
      groups: any
        ? {
            positives: parseNumberList(positiveText),
            negatives: parseNumberList(negativeText),
          }
        : null,
      problem: null,
    };
  }
  if (read.unreadable) {
    return {
      groups: null,
      problem: t(strings.unreadable, {
        line: fmt(read.unreadable.line),
        text: read.unreadable.text,
      }),
    };
  }
  if (classes.length > 0 && classes.length !== 2) {
    return {
      groups: null,
      problem: t(strings.needTwoClasses, { count: fmt(classes.length) }),
    };
  }
  return {
    groups:
      classes.length === 2
        ? splitByClass(read.scores, read.classes, positiveLabel)
        : null,
    problem: null,
  };
}

export default function RocCurveCalculatorWidget() {
  const strings = useToolUi(ROC_CURVE_CALCULATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [mode, setMode] = useState<Mode>("columns");
  const [text, setText] = useState("");
  const [header, setHeader] = useState<HeaderMode>("auto");
  const [positiveText, setPositiveText] = useState("");
  const [negativeText, setNegativeText] = useState("");
  const [chosenPositive, setChosenPositive] = useState("");
  const [direction, setDirection] = useState<"higher" | "lower">("higher");
  const [criterion, setCriterion] = useState<Criterion>("youden");
  const [cutoffText, setCutoffText] = useState("");

  const table = useMemo(() => parseLabelTable(text, header), [text, header]);
  const read = useMemo(() => readScoreRows(table.rows, table.lines), [table]);
  const classes = distinctClasses(read.classes);
  const positiveLabel = classes.includes(chosenPositive)
    ? chosenPositive
    : guessPositiveLabel(classes);

  const { groups, problem } = resolveGroups({
    mode,
    read,
    classes,
    positiveLabel,
    positiveText,
    negativeText,
    strings,
    fmt,
  });
  const analysis = useMemo(
    () =>
      groups
        ? rocAnalysis(
            groups.positives,
            groups.negatives,
            direction === "higher"
          )
        : null,
    [groups, direction]
  );
  const entered =
    mode === "columns"
      ? text.trim() !== ""
      : positiveText.trim() !== "" || negativeText.trim() !== "";
  const customCutoff = parseNumberInput(cutoffText);
  const dropped = mode === "columns" ? read.dropped : 0;

  return (
    <div className="space-y-6">
      <PillGroup
        label={strings.modeLabel}
        name="roc-mode"
        onChange={setMode}
        options={[
          { value: "columns", label: strings.modeColumns },
          { value: "lists", label: strings.modeLists },
        ]}
        value={mode}
      />
      {mode === "columns" ? (
        <PasteBox
          actions={[
            {
              label: strings.loadExample,
              onClick: () => {
                const rows = [
                  ...EXAMPLE_POSITIVE.map(
                    (v) => `${v}\t${strings.examplePositiveClass}`
                  ),
                  ...EXAMPLE_NEGATIVE.map(
                    (v) => `${v}\t${strings.exampleNegativeClass}`
                  ),
                ];
                setHeader("auto");
                setText([strings.exampleHeader, ...rows].join("\n"));
                setChosenPositive("");
              },
            },
            { label: strings.clear, onClick: () => setText("") },
          ]}
          header={{
            label: strings.headerLabel,
            value: header,
            onChange: setHeader,
            options: [
              { value: "auto", label: strings.headerAuto },
              { value: "yes", label: strings.headerYes },
              { value: "no", label: strings.headerNo },
            ],
          }}
          hint={strings.columnsHint}
          id="roc-data"
          label={strings.columnsLabel}
          onChange={setText}
          placeholder={strings.columnsPlaceholder}
          rows={8}
          value={text}
        />
      ) : (
        <div className="space-y-3">
          <div className="grid gap-4 sm:grid-cols-2">
            <TextAreaField
              hint={strings.listHint}
              id="roc-positive"
              label={strings.listPositiveLabel}
              onChange={setPositiveText}
              placeholder={strings.listPlaceholder}
              rows={6}
              value={positiveText}
            />
            <TextAreaField
              hint={strings.listHint}
              id="roc-negative"
              label={strings.listNegativeLabel}
              onChange={setNegativeText}
              placeholder={strings.listPlaceholder}
              rows={6}
              value={negativeText}
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              className={BUTTON_SMALL}
              onClick={() => {
                setPositiveText(EXAMPLE_POSITIVE.join(", "));
                setNegativeText(EXAMPLE_NEGATIVE.join(", "));
              }}
              type="button"
            >
              {strings.loadExample}
            </button>
            <button
              className={BUTTON_SMALL}
              onClick={() => {
                setPositiveText("");
                setNegativeText("");
              }}
              type="button"
            >
              {strings.clear}
            </button>
          </div>
        </div>
      )}
      <div className="grid gap-4 sm:grid-cols-2">
        {mode === "columns" && classes.length === 2 ? (
          <SelectField
            id="roc-positive-class"
            label={strings.positiveClassLabel}
            onChange={setChosenPositive}
            options={classes.map((value) => ({ value, label: value }))}
            value={positiveLabel}
          />
        ) : null}
        <SelectField
          id="roc-criterion"
          label={strings.criterionLabel}
          onChange={setCriterion}
          options={[
            { value: "youden", label: strings.criterionYouden },
            { value: "topLeft", label: strings.criterionTopLeft },
          ]}
          value={criterion}
        />
        <TextField
          hint={strings.cutoffHint}
          id="roc-cutoff"
          label={strings.cutoffLabel}
          numeric
          onChange={setCutoffText}
          value={cutoffText}
        />
      </div>
      <PillGroup
        label={strings.directionLabel}
        name="roc-direction"
        onChange={setDirection}
        options={[
          { value: "higher", label: strings.directionHigher },
          { value: "lower", label: strings.directionLower },
        ]}
        value={direction}
      />
      {groups && entered ? (
        <p className={HINT}>
          {t(strings.groupsRead, {
            positive: fmt(groups.positives.length),
            negative: fmt(groups.negatives.length),
            total: fmt(groups.positives.length + groups.negatives.length),
          })}{" "}
          {dropped > 0
            ? tPlural(strings.droppedRows, locale, dropped, {
                count: fmt(dropped),
              }).trim()
            : ""}
        </p>
      ) : null}
      {problem ? <ErrorNotice>{problem}</ErrorNotice> : null}
      {entered && !problem && groups && !analysis ? (
        <ErrorNotice>{strings.needTwoEach}</ErrorNotice>
      ) : null}
      {analysis && groups ? (
        <RocSummary
          analysis={analysis}
          criterion={criterion}
          customCutoff={customCutoff}
          fmt={fmt}
          negatives={groups.negatives}
          positives={groups.positives}
          strings={strings}
          svgRef={svgRef}
        />
      ) : null}
      {entered ? null : <Notice>{strings.enterPrompt}</Notice>}
    </div>
  );
}
